/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDecisionMatrix, fetchDecisionYears } from "../services/decisionsDataService";
import { DETAIL_ROUTES } from "../services/detailApi";
import useInvelpsTheme from "../../../hooks/useInvelpsTheme";
import { MARKET_LIGHT } from "../../../styles/invelpsMarketTheme";

const DETAIL_CARD_ROUTES = {
  FondamentauxCard: "fundamentals",
  TimingMarcheCard: "timing",
  MarketStructureCard: "market",
};

// ════════════════════════════════════════════════════════════════
// THÈME — jour / nuit
// ════════════════════════════════════════════════════════════════
function getTheme(dark) {
  return dark ? {
    appBg:       "linear-gradient(135deg,#0b1220 0%,#0f172a 100%)",
    modalBg:     "rgba(10,16,30,0.98)",
    modalBorder: "rgba(148,163,184,0.13)",
    sep:         "rgba(148,163,184,0.09)",
    textStrong:  "#e2e8f0",
    text:        "#cbd5e1",
    muted:       "rgba(148,163,184,0.55)",
    mutedFaint:  "rgba(148,163,184,0.35)",
    rowBg:       "rgba(255,255,255,0.03)",
    infoBg:      "rgba(111,138,91,0.07)",
    infoBorder:  "rgba(111,138,91,0.15)",
    tooltipBg:   "rgba(8,12,20,0.97)",
    tooltipBdr:  "rgba(148,163,184,0.18)",
    tooltipText: "#cbd5e1",
    btnBg:       "rgba(255,255,255,0.05)",
    btnBdr:      "rgba(148,163,184,0.12)",
    legendBg:    "rgba(255,255,255,0.03)",
    legendBdr:   "rgba(148,163,184,0.08)",
  } : {
    appBg:       "linear-gradient(135deg,#f6f5f1 0%,#eeece6 100%)",
    modalBg:     "rgba(250,249,245,0.99)",
    modalBorder: "rgba(30,42,34,0.12)",
    sep:         "rgba(30,42,34,0.08)",
    textStrong:  "#1f2a24",
    text:        "#243028",
    muted:       "rgba(80,100,88,0.70)",
    mutedFaint:  "rgba(80,100,88,0.45)",
    rowBg:       "rgba(30,42,34,0.03)",
    infoBg:      "rgba(111,138,91,0.07)",
    infoBorder:  "rgba(111,138,91,0.20)",
    tooltipBg:   "rgba(20,30,24,0.96)",
    tooltipBdr:  "rgba(111,138,91,0.25)",
    tooltipText: "#e2f0e8",
    btnBg:       "rgba(30,42,34,0.05)",
    btnBdr:      "rgba(30,42,34,0.12)",
    legendBg:    "rgba(30,42,34,0.03)",
    legendBdr:   "rgba(30,42,34,0.07)",
  };
}

// ════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ════════════════════════════════════════════════════════════════
const C = { green:"#6f8a5b", green2:"#8da57a", olive:"#a9b38a", amber:"#caa85e", orange:"#d28a52", red:"#c6523f" };

const TONE_COLORS = {
  green:  { bg:"rgba(111,138,91,0.12)",  border:"rgba(111,138,91,0.35)", text:"#6f8a5b" },
  amber:  { bg:"rgba(202,168,94,0.12)",  border:"rgba(202,168,94,0.35)", text:"#caa85e" },
  orange: { bg:"rgba(210,138,82,0.12)",  border:"rgba(210,138,82,0.35)", text:"#d28a52" },
  red:    { bg:"rgba(198,82,63,0.12)",   border:"rgba(198,82,63,0.35)",  text:"#c6523f" },
};
const SIGNAL_CFG = {
  yes:   { icon:"✓", label:"Favorable",    color:C.green  },
  watch: { icon:"◐", label:"Vigilance",    color:C.amber  },
  no:    { icon:"✕", label:"Défavorable",  color:C.red    },
};

// ════════════════════════════════════════════════════════════════
// DATA — COFA.PA depuis StockDashboard_Decisions
// ════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════
// DATA — alimentation PROD via API (StockDashboard_Decisions)
// Le mock statique (DD_DATASET) a été retiré : les données viennent
// désormais de decisionsDataService.js (fetchDecisionMatrix).
// Ajuster SYMBOL / AVAILABLE_YEARS selon le contexte réel de la page
// (ex: symbole sélectionné ailleurs dans l'app, ou prop du composant).
// ════════════════════════════════════════════════════════════════
const SYMBOL = "COFA.PA";
const AVAILABLE_YEARS = [2024, 2025];

// ════════════════════════════════════════════════════════════════
// MAPPING DD → 5 AXES MATRICE
// ════════════════════════════════════════════════════════════════
function buildMatrixSignals(dd) {
  return {
    fondamentaux: dd.dd_value_creation_status,
    valorisation: dd.dd_valuation_status,
    marche:       dd.dd_marche_status,
    momentum:     dd.dd_momentum_status,
    risque:       dd.dd_rr_status,
    _scores:   { fondamentaux:dd.radar_fondamentaux, valorisation:dd.radar_valorisation, marche:dd.radar_marche, momentum:dd.radar_momentum, risque:dd.radar_risque_rendement },
    _tooltips: { fondamentaux:dd.dd_value_creation_tooltip, valorisation:dd.dd_valuation_tooltip, marche:dd.dd_marche_tooltip, momentum:dd.dd_momentum_tooltip, risque:dd.dd_rr_tooltip },
    _labels:   { fondamentaux:dd.dd_value_creation_label, valorisation:dd.dd_valuation_label, marche:dd.dd_marche_label, momentum:dd.dd_momentum_label, risque:dd.dd_rr_label },
    _moatStatus: dd.dd_moat_status,
    _moatLabel:  dd.dd_moat_label,
  };
}

// ════════════════════════════════════════════════════════════════
// CARDS pour l'ordre de lecture
// ════════════════════════════════════════════════════════════════
const ALL_CARDS = [
  { id:"RisqueRendementCard",        label:"Risque/Rendement",     desc:"Veto ou go — Sharpe, drawdown, CAGR" },
  { id:"FondamentauxCard",           label:"Fondamentaux",         desc:"Qualité du business — ROIC, marges, cash flow" },
  { id:"ValorisationCard",           label:"Valorisation",         desc:"Marge de sécurité — DCF, multiples, MOS" },
  { id:"EtatFinancierCard",          label:"États Financiers",     desc:"Solidité du bilan — dette, liquidité, levier" },
  { id:"FacteursDecisionCard",       label:"Facteurs de Décision", desc:"Synthèse — EVA, moat, signal global" },
  { id:"MarketStructureCard",        label:"Market Structure",     desc:"Phase de cycle — flux, rotation, macro" },
  { id:"TimingMarcheCard",           label:"Timing",               desc:"Setup d'entrée — SMA, Wyckoff, momentum" },
  { id:"BacktestCard",               label:"Backtest",             desc:"Comportement historique — CAGR vs indice" },
  { id:"DistributionCard",           label:"Distribution",         desc:"Rendement total — dividendes, yield" },
  { id:"ProfilDescriptionRadarCard", label:"Profil",               desc:"Contexte — secteur, style, IScore" },
];

// ════════════════════════════════════════════════════════════════
// MOTEUR DIAGNOSTIC
// ════════════════════════════════════════════════════════════════
function getDiagnostic(s) {
  const { fondamentaux:f, valorisation:v, marche:m, momentum:mo, risque:r, _moatStatus:moat } = s;

  if (r==="no") return {
    verdict:"VETO — RISQUE", tone:"red", emoji:"🚫", action:"ÉLIMINER",
    decision:"Ne pas entrer", sizing:"0%", horizon:"—",
    rationale:"Le profil risque/rendement est rédhibitoire indépendamment de tout autre signal. Un Sharpe négatif et un drawdown extrême indiquent une destruction de capital structurelle. Ce filtre est absolu : même un excellent fondamental ne compense pas un profil de risque destructeur.",
    readingOrder:["RisqueRendementCard","BacktestCard","FacteursDecisionCard"],
    readingNote:"Confirmer l'ampleur du risque sur 2 cards, puis consulter la synthèse avant de classer.",
  };

  if (f==="yes"&&v==="yes"&&m==="yes"&&mo==="yes"&&r==="yes") return {
    verdict:"CONVICTION MAXIMALE", tone:"green", emoji:"✦", action:"ACHETER",
    decision:"Entrée immédiate", sizing:"Taille pleine (100%)", horizon:"12–36 mois",
    rationale:"Configuration idéale : les 5 axes sont favorables. Le business crée de la valeur (ROIC > WACC), le titre est décoté, le marché est en phase d'accumulation, le momentum confirme la tendance, et le profil risque est sain. C'est le dossier qu'on attend. Entrer avec conviction, taille pleine, thèse documentée par écrit avant d'entrer." + (moat==="yes"?" Le moat confirme que l'avantage concurrentiel est durable — il réduit le risque d'érosion de la thèse.":""),
    readingOrder:["FacteursDecisionCard","FondamentauxCard","ValorisationCard","RisqueRendementCard","MarketStructureCard","TimingMarcheCard","EtatFinancierCard","BacktestCard","DistributionCard"],
    readingNote:"Lecture complète dans l'ordre structurel → tactique pour valider chaque pilier avant d'entrer.",
  };

  if (f==="yes"&&v==="yes"&&r!=="no"&&mo!=="yes") return {
    verdict:"ATTENTE SETUP", tone:"amber", emoji:"⏳",
    action: m==="yes"?"INITIER (30%)":"SURVEILLER",
    decision: m==="yes"?"Position d'initiation 30–40%":"Surveiller — pas d'entrée",
    sizing: m==="yes"?"30–40% de la taille cible":"0%", horizon:"3–6 mois max",
    rationale:"Les fondamentaux et la valorisation valident la thèse — mais le momentum ne confirme pas encore l'entrée. C'est le cas le plus courant pour un stock-picker rigoureux. La bonne pratique est de construire la thèse complète maintenant (prix cible, catalyseur, invalidation), puis de poser une alarme sur des conditions techniques précises : franchissement de la SMA50, compression de volatilité qui se résout, apparition de volume d'accumulation. On ne rentre pas sur anticipation — on rentre sur confirmation." + (m==="yes"?" Le marché est favorable : une position d'initiation réduite (30–40%) permet de rester vigilant sans surinvestir.":" Le marché est défavorable : rester hors position, le timing coûte cher quand tout va dans le mauvais sens."),
    readingOrder:["FondamentauxCard","ValorisationCard","RisqueRendementCard","EtatFinancierCard","MarketStructureCard","TimingMarcheCard","BacktestCard","DistributionCard"],
    readingNote:"Valider la thèse fondamentale d'abord (cards 1–4), surveiller le timing en dernier (cards 5–6).",
  };

  if (f==="no"&&(mo==="yes"||mo==="watch")&&m==="yes") return {
    verdict:"MOUVEMENT SPÉCULATIF", tone:"red", emoji:"⚠", action:"ÉVITER",
    decision:"Ne pas entrer en investissement", sizing:"0%", horizon:"—",
    rationale:"Le momentum est réel mais il ne repose sur aucune base fondamentale. Sur une micro-cap ou un titre illiquide, ce profil se termine presque toujours par un retour violent à la moyenne — souvent en quelques semaines. Le mouvement est alimenté par du flux spéculatif, pas par de la création de valeur. Ce n'est pas un investissement. Un opérateur expérimenté peut envisager un trade court avec stop serré — mais c'est une posture de trading actif, pas d'investissement.",
    readingOrder:["RisqueRendementCard","BacktestCard","TimingMarcheCard","MarketStructureCard"],
    readingNote:"Évaluer l'ampleur du risque de retournement avant toute autre considération.",
  };

  if (f==="yes"&&v==="yes"&&m==="no"&&mo==="no") return {
    verdict:"VALUE TRAP POTENTIEL", tone:"orange", emoji:"🪤", action:"ATTENDRE",
    decision:"Ne pas entrer", sizing:"0%", horizon:"Réexaminer 6–12 mois",
    rationale:"Configuration classique du value trap : le titre semble bon marché et les fondamentaux sont solides, mais le marché et le momentum vont dans la mauvaise direction. Le risque n'est pas de perdre sur la thèse — c'est d'immobiliser du capital pendant 12–18 mois sans performance, alors que d'autres idées sont disponibles. Le capital immobilisé a un coût d'opportunité réel. Attendre un signal de retournement marché (secteur qui rentre en accumulation, flux entrants) avant d'engager." + (moat==="yes"?" Le moat protège la thèse sur le long terme — la patience est justifiée si le bilan le permet.":" L'absence de moat renforce le risque que la décote soit permanente."),
    readingOrder:["FondamentauxCard","ValorisationCard","MarketStructureCard","TimingMarcheCard","EtatFinancierCard","RisqueRendementCard","BacktestCard"],
    readingNote:"Vérifier si le fondamental justifie la patience, puis surveiller les signaux de retournement marché.",
  };

  if (f==="watch"&&v==="yes"&&m==="yes"&&mo==="yes") return {
    verdict:"CONVICTION MODÉRÉE", tone:"amber", emoji:"◑", action:"INITIER (50%)",
    decision:"Entrée réduite", sizing:"50–60% de la taille cible", horizon:"6–18 mois",
    rationale:"Le setup marché et le timing sont favorables, la valorisation est attractive — mais les fondamentaux sont en transition ou mitigés. C'est le risque central de ce dossier : l'entreprise n'a pas encore prouvé qu'elle peut créer de la valeur durablement. La bonne posture est d'entrer avec une taille réduite, de surveiller les publications financières (ROIC, marges, FCF) de très près, et d'augmenter la position uniquement si les fondamentaux se confirment sur 1–2 trimestres.",
    readingOrder:["FondamentauxCard","EtatFinancierCard","ValorisationCard","RisqueRendementCard","MarketStructureCard","TimingMarcheCard","DistributionCard","BacktestCard"],
    readingNote:"Fondamentaux = incertitude centrale — les lire et les challenger en premier.",
  };

  if (f==="yes"&&v==="watch"&&m==="yes"&&mo==="yes"&&r==="yes") return {
    verdict:"MOMENTUM PLAY", tone:"amber", emoji:"◈", action:"ENTRER (prudent)",
    decision:"Entrer avec stop serré", sizing:"50–70% de la taille cible", horizon:"3–9 mois",
    rationale:"Le dossier est bien positionné sur tous les axes sauf la valorisation, qui est proche de la valeur juste. La marge de sécurité est faible ou nulle. On peut profiter du momentum — mais ce n'est plus un investissement de conviction sur la valeur, c'est un trade sur la qualité du business. La discipline est essentielle : définir à l'avance le point d'invalidation (ex. momentum qui se brise, SMA50 perdue) et sortir sans hésiter si ce point est atteint.",
    readingOrder:["ValorisationCard","TimingMarcheCard","MarketStructureCard","FondamentauxCard","RisqueRendementCard","EtatFinancierCard"],
    readingNote:"La valorisation est le risque central ici — la lire et la challenger en premier, puis valider le timing.",
  };

  const vals=[f,v,m,mo,r];
  const yesC=vals.filter(x=>x==="yes").length;
  const noC =vals.filter(x=>x==="no").length;
  const watchC=vals.filter(x=>x==="watch").length;

  if (noC>=2) return {
    verdict:"DOSSIER DÉFAVORABLE", tone:"red", emoji:"✕", action:"ÉVITER",
    decision:"Ne pas entrer", sizing:"0%", horizon:"—",
    rationale:`${noC} axes défavorables sur 5. Un bon investissement nécessite au moins 3 axes clairement favorables, sans veto fort. En dessous de ce seuil, le rapport conviction/risque ne justifie pas d'engager du capital. ` + (moat==="yes"?`Le moat est présent et constitue un point d'appui réel — mais il est insuffisant seul pour compenser ${noC} signaux négatifs. Ce titre peut rejoindre la liste de surveillance.`:"Passer à un dossier plus convaincant."),
    readingOrder:["RisqueRendementCard","FondamentauxCard","FacteursDecisionCard","MarketStructureCard","TimingMarcheCard"],
    readingNote:"Confirmer rapidement les points d'échec avant d'éliminer — ne pas s'attarder.",
  };

  if (yesC>=3&&noC===0) return {
    verdict:"DOSSIER SOLIDE", tone:"green", emoji:"✓", action:"INITIER",
    decision:"Entrée avec taille réduite", sizing:"50–70% de la taille cible", horizon:"6–24 mois",
    rationale:`${yesC} axes favorables, ${watchC} en vigilance. Dossier solide mais pas encore complet. Entrer avec une taille réduite (50–70%), documenter la thèse, et compléter la position uniquement quand les axes en vigilance se clarifieront dans le bon sens. Ne pas compléter si les axes en vigilance se dégradent.`,
    readingOrder:["FondamentauxCard","ValorisationCard","RisqueRendementCard","MarketStructureCard","TimingMarcheCard","EtatFinancierCard","BacktestCard","DistributionCard"],
    readingNote:"Ordre standard structurel → tactique.",
  };

  return {
    verdict:"CAS PARTICULIER", tone:"amber", emoji:"?", action:"ANALYSER",
    decision:"Analyse approfondie requise", sizing:"—", horizon:"—",
    rationale:"Cette combinaison ne correspond à aucun pattern standard. Elle nécessite une analyse cas par cas, en creusant notamment les axes en vigilance pour comprendre s'ils sont en train de s'améliorer ou de se dégrader. La direction de la tendance sur chaque axe est souvent plus importante que le niveau absolu.",
    readingOrder:ALL_CARDS.map(c=>c.id),
    readingNote:"Ordre standard de prudence : risque et fondamentaux en premier.",
  };
}

// ════════════════════════════════════════════════════════════════
// TOOLTIP
// ════════════════════════════════════════════════════════════════
function TipIcon({ text, th }) {
  const [v, setV] = useState(false);
  const [pos, setPos] = useState({x:0,y:0});
  const ref = useRef(null);
  const upd = e => { const r=ref.current?.getBoundingClientRect(); if(r) setPos({x:e.clientX-r.left,y:e.clientY-r.top}); };
  return (
    <span ref={ref} style={{position:"relative",display:"inline-flex",cursor:"help",marginLeft:3,verticalAlign:"middle"}}
      onMouseEnter={e=>{setV(true);upd(e);}} onMouseMove={upd} onMouseLeave={()=>setV(false)}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{opacity:0.4}}>
        <circle cx="6" cy="6" r="5.5" stroke="currentColor" strokeWidth="1"/>
        <text x="6" y="9" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="currentColor" fontFamily="ui-sans-serif">i</text>
      </svg>
      {v && <div style={{position:"absolute",left:pos.x+10,top:pos.y-6,zIndex:9999,background:th.tooltipBg,border:`1px solid ${th.tooltipBdr}`,borderRadius:10,padding:"8px 12px",maxWidth:240,fontSize:11,lineHeight:1.55,color:th.tooltipText,boxShadow:"0 8px 28px rgba(0,0,0,0.3)",pointerEvents:"none",backdropFilter:"blur(10px)",whiteSpace:"normal"}}>{text}</div>}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════
// MATRICE MODALE
// ════════════════════════════════════════════════════════════════
function MatrixModal({ signals, diagnostic, dd, onClose, th, dark, calendarYear, availableYears, onYearChange, pageMode = false, symbol, onOpenDetail }) {
  useEffect(() => {
    const h = e => { if(e.key==="Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const [tab, setTab] = useState("matrix");
  const [hovered, setHovered] = useState(null);

  const AXES_DISPLAY = [
    {
      id:"marche", label:"Marché", icon:"◎", cardRef:"MarketStructureCard",
      pedagogie:"Analyse l'environnement macro et sectoriel : est-ce que le marché accueille ou rejette ce type d'actif en ce moment ? Un bon titre dans un mauvais marché reste un mauvais investissement à court terme.",
    },
    {
      id:"fondamentaux", label:"Fondamentaux", icon:"◈", cardRef:"FondamentauxCard",
      pedagogie:"Mesure la qualité intrinsèque du business : est-ce que l'entreprise crée vraiment de la valeur ? Le ratio clé est ROIC vs WACC — si le retour sur capital investi est inférieur au coût du capital, l'entreprise détruit de la valeur même si elle fait des profits.",
    },
    {
      id:"momentum", label:"Momentum", icon:"◆", cardRef:"TimingMarcheCard",
      pedagogie:"Évalue le timing d'entrée : est-ce que le marché commence à reconnaître la valeur du titre ? Le momentum ne valide pas la thèse — il conditionne le rendement réel. Un bon titre qui reste en tendance baissière peut rester un mauvais investissement pendant 18 mois.",
    },
    {
      id:"risque", label:"Risque/Rendement", icon:"◐", cardRef:"RisqueRendementCard",
      pedagogie:"Filtre absolu : le profil historique risque/rendement est-il acceptable ? Un Sharpe négatif ou un drawdown de -80% signalent une destruction de capital structurelle — peu importe les autres signaux. C'est le premier veto possible.",
    },
    {
      id:"valorisation", label:"Valorisation", icon:"◉", cardRef:"ValorisationCard",
      pedagogie:"Mesure la marge de sécurité : à quel prix le marché valorise-t-il le titre par rapport à sa valeur intrinsèque (DCF) ? Une décote (MOS > 0) protège contre les erreurs d'estimation. Une surévaluation réduit le potentiel et amplifie le risque de perte.",
    },
  ];

  const orderedCards = diagnostic.readingOrder
    .map((id,i) => ({ ...ALL_CARDS.find(c=>c.id===id), rank:i+1 }))
    .filter(c=>c.id);
  const remainingCards = ALL_CARDS.filter(c => !diagnostic.readingOrder.includes(c.id));

  return (
    <div onClick={pageMode ? undefined : (e=>{ if(e.target===e.currentTarget) onClose(); })} style={pageMode ? {
      width:"100%",maxWidth:920,margin:"0 auto",padding:"12px 14px 48px",
      fontFamily:"ui-sans-serif,system-ui,sans-serif",
    } : {
      position:"fixed",inset:0,zIndex:500,
      background:dark?"rgba(8,12,20,0.92)":"rgba(20,34,26,0.60)",
      backdropFilter:"blur(16px)",
      display:"flex",alignItems:"center",justifyContent:"center",
      padding:"20px 12px",
    }}>
      <div style={{
        width:"100%",maxWidth:pageMode?920:820,maxHeight:pageMode?"none":"90vh",
        background:th.modalBg,
        border:`1px solid ${th.modalBorder}`,
        borderRadius:22,
        boxShadow:dark
          ?"0 48px 120px rgba(0,0,0,0.65), 0 0 0 1px rgba(111,138,91,0.07)"
          :"0 32px 80px rgba(20,40,30,0.22)",
        display:"flex",flexDirection:"column",
        overflow:"hidden",
        fontFamily:"ui-sans-serif,system-ui,sans-serif",
        animation:"matrixIn 0.28s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <style>{`@keyframes matrixIn{from{opacity:0;transform:translateY(14px) scale(0.985)}to{opacity:1;transform:none}}`}</style>

        {/* ── Header ───────────────────────────── */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"15px 20px",borderBottom:`1px solid ${th.sep}`,flexShrink:0,gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
            <div style={{width:36,height:36,borderRadius:10,background:"rgba(111,138,91,0.13)",border:"1px solid rgba(111,138,91,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,color:C.green,fontWeight:700,flexShrink:0}}>⬡</div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:14,fontWeight:800,color:th.textStrong,letterSpacing:"-0.02em"}}>Matrice de Décision — Auto</div>
              <div style={{fontSize:9.5,color:th.mutedFaint,marginTop:2,fontFamily:"ui-monospace,monospace",lineHeight:1.4,wordBreak:"break-all"}}>
                {dd.dd_signal} · {dd.dd_global_score}/100
              </div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            {availableYears?.length > 1 && (
              <div style={{display:"flex",gap:3}}>
                {availableYears.map(y=>(
                  <button key={y} type="button" onClick={()=>onYearChange?.(y)} style={{
                    padding:"3px 9px",borderRadius:7,fontSize:10.5,fontWeight:700,
                    fontFamily:"ui-monospace,monospace",cursor:"pointer",
                    background:calendarYear===y?`${C.green}20`:"none",
                    border:`1px solid ${calendarYear===y?C.green+"55":th.sep}`,
                    color:calendarYear===y?C.green:th.mutedFaint,
                  }}>{y}</button>
                ))}
              </div>
            )}
            <button type="button" onClick={onClose} style={{width:30,height:30,borderRadius:8,cursor:"pointer",background:th.btnBg,border:`1px solid ${th.btnBdr}`,color:th.muted,fontSize:17,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
          </div>
        </div>

        {/* ── Tabs ─────────────────────────────── */}
        <div style={{display:"flex",borderBottom:`1px solid ${th.sep}`,padding:"0 20px",flexShrink:0}}>
          {[{id:"matrix",label:"Configuration"},{id:"reading",label:"Ordre de lecture"}].map(t=>(
            <button key={t.id} type="button" onClick={()=>setTab(t.id)} style={{
              padding:"9px 14px",fontSize:12,fontWeight:600,
              color:tab===t.id?C.green:th.muted,
              background:"none",border:"none",cursor:"pointer",
              borderBottom:tab===t.id?`2px solid ${C.green}`:"2px solid transparent",
              marginBottom:-1,transition:"all 0.15s",
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── Body ─────────────────────────────── */}
        <div style={{flex:1,overflow:"auto",padding:"16px 20px"}}>

          {/* ══ TAB CONFIG ══════════════════════ */}
          {tab==="matrix" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>

              {/* Info auto */}
              <div style={{padding:"9px 13px",borderRadius:10,background:th.infoBg,border:`1px solid ${th.infoBorder}`,fontSize:11,color:th.muted,lineHeight:1.5,display:"flex",alignItems:"flex-start",gap:8}}>
                <span style={{fontSize:13,color:C.green,flexShrink:0,marginTop:1}}>⬡</span>
                <span>Signaux <strong style={{color:th.text}}>calculés automatiquement</strong>. Aucune saisie manuelle — les statuts proviennent directement de <code style={{fontFamily:"ui-monospace,monospace",color:C.olive,fontSize:10}}>StockDashboard_Decisions</code>.</span>
              </div>

              {/* 5 axes */}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {AXES_DISPLAY.map((axis) => {
                  const sig   = signals[axis.id];
                  const sc    = SIGNAL_CFG[sig];
                  const score = signals._scores[axis.id];
                  const label = signals._labels[axis.id];
                  const tip   = signals._tooltips[axis.id];
                  const barW  = Math.max(3, score);
                  return (
                    <div key={axis.id} style={{
                      borderRadius:13,overflow:"hidden",
                      background:th.rowBg,
                      border:`1px solid ${sc.color}30`,
                    }}>
                      {/* Ligne titre */}
                      <div style={{display:"flex",alignItems:"center",padding:"10px 13px 6px",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:13,color:C.green,lineHeight:1,flexShrink:0}}>{axis.icon}</span>
                        <span style={{fontSize:13,fontWeight:800,color:th.textStrong,flex:1,minWidth:80}}>{axis.label}</span>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                          <span style={{fontSize:9,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,background:th.rowBg,padding:"1px 5px",borderRadius:4,border:`1px solid ${th.sep}`,whiteSpace:"nowrap"}}>{axis.cardRef}</span>
                          {pageMode && symbol && DETAIL_CARD_ROUTES[axis.cardRef] && onOpenDetail ? (
                            <button type="button" onClick={() => onOpenDetail(DETAIL_CARD_ROUTES[axis.cardRef])} style={{
                              fontSize:9, fontWeight:700, fontFamily:"ui-monospace,monospace",
                              color:C.green, background:`${C.green}12`, border:`1px solid ${C.green}33`,
                              padding:"2px 8px", borderRadius:6, cursor:"pointer", whiteSpace:"nowrap",
                            }}>Analyse détaillée →</button>
                          ) : null}
                          <span style={{fontFamily:"ui-monospace,monospace",fontSize:10.5,fontWeight:700,color:sc.color,whiteSpace:"nowrap"}}>{score?.toFixed(1)}/100</span>
                          <span style={{
                            display:"inline-flex",alignItems:"center",gap:4,
                            fontSize:10,fontWeight:800,
                            color:sc.color,background:`${sc.color}18`,border:`1px solid ${sc.color}44`,
                            padding:"3px 10px",borderRadius:99,
                            whiteSpace:"nowrap",flexShrink:0,
                          }}>
                            {sc.icon} {sc.label}
                          </span>
                        </div>
                      </div>

                      {/* Label source + tooltip */}
                      <div style={{padding:"0 13px 5px",fontSize:10.5,color:th.muted,lineHeight:1.4,display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontStyle:"italic"}}>{label}</span>
                        <TipIcon text={tip} th={th}/>
                      </div>

                      {/* Barre de score */}
                      <div style={{padding:"0 13px 10px"}}>
                        <div style={{height:3,borderRadius:99,background:th.sep}}>
                          <div style={{height:"100%",borderRadius:99,width:`${barW}%`,background:sc.color,opacity:0.65,transition:"width 0.7s ease"}}/>
                        </div>
                      </div>

                      {/* Pédagogie — explication de l'axe */}
                      <div style={{
                        padding:"8px 13px 10px",
                        borderTop:`1px solid ${th.sep}`,
                        background:dark?"rgba(255,255,255,0.015)":"rgba(30,42,34,0.025)",
                      }}>
                        <div style={{fontSize:9,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,fontWeight:700,letterSpacing:"0.07em",marginBottom:4}}>POURQUOI CET AXE ?</div>
                        <p style={{fontSize:10.5,color:th.muted,lineHeight:1.55,margin:0}}>{axis.pedagogie}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Moat */}
              <div style={{padding:"9px 13px",borderRadius:10,background:`${C.amber}0a`,border:`1px solid ${C.amber}22`,fontSize:10.5,color:th.muted,lineHeight:1.55}}>
                <span style={{fontWeight:700,color:C.amber}}>Moat · {signals._moatStatus==="yes"?"Favorable ✓":"Vigilance ◐"} </span>
                — {signals._moatLabel}
                <div style={{marginTop:5,fontSize:10,color:th.mutedFaint,lineHeight:1.4}}>
                  Le moat (avantage concurrentiel) enrichit le raisonnement fondamental mais n'est pas un axe matrice autonome. Un Narrow moat signifie un avantage réel mais limité dans le temps ou face à la concurrence. Un Wide moat est une forteresse durable. L'absence de moat augmente le risque d'érosion de la thèse.
                </div>
              </div>

              {/* Diagnostic */}
              {(() => {
                const dcol2 = TONE_COLORS[diagnostic.tone] || TONE_COLORS.amber;
                return (
                  <div style={{borderRadius:14,border:`2px solid ${dcol2.border}`,background:dcol2.bg,overflow:"hidden"}}>
                    {/* Verdict */}
                    <div style={{padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${dcol2.border}`,gap:10,flexWrap:"wrap"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:22,lineHeight:1}}>{diagnostic.emoji}</span>
                        <div>
                          <div style={{fontSize:15,fontWeight:800,color:dcol2.text,letterSpacing:"-0.02em"}}>{diagnostic.verdict}</div>
                          <div style={{fontSize:9.5,color:th.mutedFaint,marginTop:1,fontFamily:"ui-monospace,monospace"}}>dd_signal: {dd.dd_signal} · score: {dd.dd_global_score}/100</div>
                        </div>
                      </div>
                      <div style={{padding:"6px 14px",borderRadius:8,background:dcol2.text,color:"#fff",fontSize:11,fontWeight:800,letterSpacing:"0.05em",flexShrink:0}}>{diagnostic.action}</div>
                    </div>

                    {/* KPIs */}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",borderBottom:`1px solid ${dcol2.border}`}}>
                      {[{l:"DÉCISION",v:diagnostic.decision},{l:"SIZING",v:diagnostic.sizing},{l:"HORIZON",v:diagnostic.horizon}].map((k,i)=>(
                        <div key={k.l} style={{padding:"9px 13px",borderRight:i<2?`1px solid ${dcol2.border}`:"none"}}>
                          <div style={{fontSize:8.5,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,fontWeight:700,letterSpacing:"0.07em",marginBottom:3}}>{k.l}</div>
                          <div style={{fontSize:11.5,fontWeight:700,color:dcol2.text,lineHeight:1.3}}>{k.v}</div>
                        </div>
                      ))}
                    </div>

                    {/* Raisonnement */}
                    <div style={{padding:"11px 16px",borderBottom:`1px solid ${dcol2.border}`}}>
                      <div style={{fontSize:8.5,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,fontWeight:700,letterSpacing:"0.07em",marginBottom:6}}>RAISONNEMENT</div>
                      <p style={{fontSize:12,color:th.text,lineHeight:1.7,margin:0}}>{diagnostic.rationale}</p>
                    </div>

                    {/* Ordre preview */}
                    <div style={{padding:"10px 16px 13px"}}>
                      <div style={{fontSize:8.5,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,fontWeight:700,letterSpacing:"0.07em",marginBottom:7}}>ORDRE DE LECTURE RECOMMANDÉ</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                        {diagnostic.readingOrder.map((id,i) => {
                          const card=ALL_CARDS.find(c=>c.id===id);
                          if(!card) return null;
                          return (
                            <div key={id} style={{
                              display:"inline-flex",alignItems:"center",gap:4,
                              padding:"3px 10px",borderRadius:99,
                              background:i===0?dcol2.text:`${dcol2.border}30`,
                              border:`1px solid ${i===0?dcol2.text:dcol2.border}`,
                              whiteSpace:"nowrap",flexShrink:0,
                            }}>
                              <span style={{fontSize:8,fontWeight:800,fontFamily:"ui-monospace,monospace",color:i===0?"#fff":dcol2.text}}>{i+1}</span>
                              <span style={{fontSize:10,fontWeight:600,color:i===0?"#fff":th.text}}>{card.label}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{fontSize:10.5,color:th.muted,marginTop:7,lineHeight:1.5}}>{diagnostic.readingNote}</div>
                      <button type="button" onClick={()=>setTab("reading")} style={{marginTop:9,padding:"6px 13px",borderRadius:8,background:"none",border:`1px solid ${dcol2.border}`,color:dcol2.text,fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        Voir l'ordre détaillé →
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ══ TAB ORDRE DE LECTURE ════════════ */}
          {tab==="reading" && (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{padding:"9px 13px",borderRadius:10,background:th.infoBg,border:`1px solid ${th.infoBorder}`,fontSize:11,color:th.muted,lineHeight:1.55}}>
                {diagnostic.readingNote}
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {orderedCards.map((card,idx)=>{
                  const isFirst=idx===0, isPrio=idx<3;
                  return (
                    <div key={card.id}
                      onMouseEnter={()=>setHovered(card.id)}
                      onMouseLeave={()=>setHovered(null)}
                      style={{
                        display:"flex",alignItems:"center",gap:10,
                        padding:"10px 13px",borderRadius:11,
                        background:hovered===card.id?"rgba(111,138,91,0.08)":isFirst?`${C.green}08`:th.rowBg,
                        border:`1px solid ${isFirst?`${C.green}28`:th.sep}`,
                        transition:"all 0.14s",
                      }}>
                      <div style={{width:28,height:28,borderRadius:8,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:isFirst?C.green:isPrio?`${C.green}18`:th.btnBg,border:`1px solid ${isFirst?C.green:isPrio?`${C.green}28`:th.sep}`,fontSize:11,fontWeight:800,fontFamily:"ui-monospace,monospace",color:isFirst?"#fff":isPrio?C.green:th.mutedFaint}}>
                        {card.rank}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12.5,fontWeight:700,color:isFirst?C.green:th.textStrong}}>{card.label}</div>
                        <div style={{fontSize:10.5,color:th.muted,marginTop:1,lineHeight:1.3}}>{card.desc}</div>
                      </div>
                      {idx===0&&<span style={{fontSize:9,fontWeight:800,color:C.green,background:`${C.green}14`,border:`1px solid ${C.green}28`,padding:"2px 8px",borderRadius:99,flexShrink:0,whiteSpace:"nowrap"}}>PRIORITÉ</span>}
                      {idx===1&&<span style={{fontSize:9,fontWeight:800,color:C.amber,background:`${C.amber}12`,border:`1px solid ${C.amber}25`,padding:"2px 8px",borderRadius:99,flexShrink:0,whiteSpace:"nowrap"}}>CRITIQUE</span>}
                    </div>
                  );
                })}

                {remainingCards.length>0&&(
                  <>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"3px 0"}}>
                      <div style={{flex:1,height:1,background:th.sep}}/>
                      <span style={{fontSize:9.5,color:th.mutedFaint,fontFamily:"ui-monospace,monospace",whiteSpace:"nowrap"}}>SECONDAIRES POUR CETTE CONFIG</span>
                      <div style={{flex:1,height:1,background:th.sep}}/>
                    </div>
                    {remainingCards.map(card=>(
                      <div key={card.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 13px",borderRadius:11,background:th.rowBg,border:`1px solid ${th.sep}`,opacity:0.5}}>
                        <div style={{width:22,height:22,borderRadius:6,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",background:th.btnBg,border:`1px solid ${th.sep}`,fontSize:10,fontFamily:"ui-monospace,monospace",color:th.mutedFaint}}>–</div>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:11.5,fontWeight:600,color:th.muted}}>{card.label}</div>
                          <div style={{fontSize:10,color:th.mutedFaint}}>{card.desc}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Légende dynamique — construite depuis le readingOrder réel */}
              {(() => {
                const total = orderedCards.length;
                const PALIER_MAP = {
                  RisqueRendementCard:        "veto",
                  BacktestCard:               "veto",
                  FondamentauxCard:           "these",
                  ValorisationCard:           "these",
                  EtatFinancierCard:          "these",
                  FacteursDecisionCard:       "these",
                  MarketStructureCard:        "tactique",
                  TimingMarcheCard:           "tactique",
                  DistributionCard:           "contexte",
                  ProfilDescriptionRadarCard: "contexte",
                };
                const PALIER_META = {
                  veto:     { label:"Veto ou go",             desc:"Ces cards peuvent éliminer le dossier immédiatement. Si elles disent non, inutile d'aller plus loin.", color:C.red    },
                  these:    { label:"Thèse fondamentale",     desc:"Qualité du business, valeur intrinsèque, solidité du bilan. Le cœur de la conviction.",               color:C.green  },
                  tactique: { label:"Positionnement tactique",desc:"Phase de cycle et setup d'entrée. Valide le moment d'agir — pas la thèse elle-même.",                 color:C.amber  },
                  contexte: { label:"Contexte complémentaire",desc:"Historique, revenus, profil global. Enrichissement de la conviction, pas élément de décision.",        color:C.olive  },
                };
                const groupes = [];
                let currentPalier = null;
                orderedCards.forEach((card, idx) => {
                  const palier = PALIER_MAP[card.id] || "contexte";
                  if (palier !== currentPalier) {
                    groupes.push({ palier, startRank: idx+1, endRank: idx+1 });
                    currentPalier = palier;
                  } else {
                    groupes[groupes.length-1].endRank = idx+1;
                  }
                });

                if (groupes.length === 0) return null;

                return (
                  <div style={{padding:"11px 15px",borderRadius:11,background:th.legendBg,border:`1px solid ${th.legendBdr}`}}>
                    <div style={{fontSize:8.5,fontFamily:"ui-monospace,monospace",color:th.mutedFaint,fontWeight:700,letterSpacing:"0.07em",marginBottom:8}}>
                      LOGIQUE DE L'ORDRE · {total} card{total>1?"s":""} pour ce diagnostic
                    </div>
                    {groupes.map((g, gi) => {
                      const meta = PALIER_META[g.palier];
                      const range = g.startRank===g.endRank ? `${g.startRank}` : `${g.startRank}–${g.endRank}`;
                      return (
                        <div key={gi} style={{display:"flex",gap:10,marginBottom:gi<groupes.length-1?8:0,alignItems:"flex-start"}}>
                          <span style={{
                            fontSize:9.5,fontFamily:"ui-monospace,monospace",fontWeight:800,
                            color:meta.color,minWidth:32,flexShrink:0,marginTop:2,
                          }}>{range}</span>
                          <div>
                            <div style={{fontSize:11,fontWeight:700,color:th.text}}>{meta.label}</div>
                            <div style={{fontSize:10.5,color:th.muted,lineHeight:1.4}}>{meta.desc}</div>
                          </div>
                        </div>
                      );
                    })}
                    {total < 10 && (
                      <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${th.sep}`,fontSize:10,color:th.mutedFaint,lineHeight:1.4}}>
                        {10-total} card{10-total>1?"s":""} secondaire{10-total>1?"s":""} non prioritaire{10-total>1?"s":""} pour ce diagnostic — visibles ci-dessus en grisé.
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — reproduit uniquement la Matrice de Décision
// ════════════════════════════════════════════════════════════════
export default function MatriceDecisionPage({ symbol, onBack }) {
  const navigate = useNavigate();
  const { darkMode: dark } = useInvelpsTheme();
  const [calendarYear, setCalendarYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [DD, setDD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const th = getTheme(dark);

  useEffect(() => {
    if (!symbol) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDecisionYears(symbol, { signal: controller.signal })
      .then((yrs) => {
        if (yrs.length) {
          setAvailableYears(yrs);
          setCalendarYear((prev) => prev ?? yrs[yrs.length - 1]);
        } else {
          setError("Aucune année de décision disponible pour ce titre.");
          setLoading(false);
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [symbol]);

  useEffect(() => {
    if (!symbol || !calendarYear) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDecisionMatrix(symbol, calendarYear, { signal: controller.signal })
      .then((dd) => {
        setDD(dd);
      })
      .catch((err) => { if (err.name !== "AbortError") setError(err.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [symbol, calendarYear]);

  const matrixSignals = DD ? buildMatrixSignals(DD) : null;
  const diagnostic = matrixSignals ? getDiagnostic(matrixSignals) : null;

  return (
    <div style={{ minHeight:"100vh", background: dark ? th.appBg : MARKET_LIGHT.appBg, fontFamily:"ui-sans-serif,system-ui,sans-serif" }}>
      <div style={{ padding:"12px 14px 0", maxWidth:920, margin:"0 auto" }}>
        <button type="button" onClick={onBack} style={{
          background:"none", border:"none", color:th.muted, fontSize:12, fontWeight:700,
          marginBottom:10, display:"flex", alignItems:"center", gap:4, padding:0, cursor:"pointer",
        }}>← Retour à l&apos;analyse</button>
      </div>
      {loading && (
        <div style={{ color:th.muted, fontSize:13, fontFamily:"ui-monospace,monospace", textAlign:"center", padding:48 }}>
          Chargement de la matrice de décision…
        </div>
      )}
      {!loading && error && (
        <div style={{
          maxWidth:420, margin:"24px auto", padding:"16px 18px", borderRadius:12,
          background:`${C.red}12`, border:`1px solid ${C.red}40`, color:C.red, fontSize:12.5, lineHeight:1.6,
        }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>Impossible de charger la matrice</div>
          {error}
        </div>
      )}
      {!loading && !error && DD && matrixSignals && (
        <MatrixModal
          pageMode
          signals={matrixSignals}
          diagnostic={diagnostic}
          dd={DD}
          onClose={onBack}
          th={th}
          dark={dark}
          symbol={symbol}
          onOpenDetail={(kind) => navigate(DETAIL_ROUTES[kind](symbol))}
          calendarYear={calendarYear || DD.calendarYear}
          availableYears={availableYears}
          onYearChange={setCalendarYear}
        />
      )}
    </div>
  );
}

